import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Upload, Plus, Lock, LogOut, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { resolveUrl } from "@/components/Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lucra_admin_token";

const authHeader = (token) => ({ headers: { "X-Admin-Token": token } });

const AdminClients = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [gate, setGate] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  // New client form
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [logo, setLogo] = useState(null);
  const logoRef = useRef(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/clients`);
      setClients(data.clients || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const unlock = (e) => {
    e.preventDefault();
    if (!gate) return;
    sessionStorage.setItem(TOKEN_KEY, gate);
    setToken(gate);
  };

  const lock = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const createClient = async (e) => {
    e.preventDefault();
    if (!name || !logo) {
      toast.error("Client name and logo are required.");
      return;
    }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      if (location) fd.append("location", location);
      fd.append("logo", logo);
      await axios.post(`${API}/clients`, fd, authHeader(token));
      toast.success("Client created.");
      setName(""); setLocation(""); setLogo(null);
      if (logoRef.current) logoRef.current.value = "";
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Create failed.";
      if (err?.response?.status === 401) {
        toast.error("Admin token rejected. Re-enter.");
        lock();
      } else {
        toast.error(detail);
      }
    } finally {
      setCreating(false);
    }
  };

  const uploadPhotos = async (slug, files) => {
    if (!files?.length) return;
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    try {
      const { data } = await axios.post(`${API}/clients/${slug}/photos`, fd, authHeader(token));
      toast.success(`Added ${data.added} photo${data.added === 1 ? "" : "s"}.`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed.";
      if (err?.response?.status === 401) { toast.error("Session expired."); lock(); }
      else toast.error(detail);
    }
  };

  const deletePhoto = async (slug, photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/clients/${slug}/photos/${photoId}`, authHeader(token));
      await load();
    } catch { toast.error("Delete failed."); }
  };

  const deleteClient = async (slug) => {
    if (!window.confirm("Delete this entire client and all photos?")) return;
    try {
      await axios.delete(`${API}/clients/${slug}`, authHeader(token));
      toast.success("Client deleted.");
      await load();
    } catch { toast.error("Delete failed."); }
  };

  // ---------- Gate ----------
  if (!token) {
    return (
      <section className="pt-40 pb-24 min-h-screen">
        <div className="max-w-[440px] mx-auto px-6 md:px-10">
          <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-6 flex items-center gap-2">
            <Lock size={12} /> Admin · Clients
          </div>
          <h1 className="font-display text-4xl text-white mb-8">Enter admin token</h1>
          <form onSubmit={unlock} className="space-y-6" data-testid="admin-gate-form">
            <input
              type="password"
              className="lucra-input"
              placeholder="Admin token"
              value={gate}
              onChange={(e) => setGate(e.target.value)}
              autoFocus
              data-testid="admin-gate-input"
            />
            <button type="submit" className="btn-gold" data-testid="admin-gate-submit"><span>Unlock</span></button>
          </form>
          <div className="mt-8 font-mono-tech text-[10px] text-white/40">
            Token lives in <code className="text-white/60">backend/.env → ADMIN_TOKEN</code>.
          </div>
        </div>
      </section>
    );
  }

  // ---------- Admin dashboard ----------
  return (
    <section className="pt-32 pb-24">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 border-b border-white/10 pb-8">
          <div>
            <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-2 flex items-center gap-2">
              <Lock size={12} /> Admin · Clients
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-white tracking-tight">Client Portfolio Manager</h1>
          </div>
          <button onClick={lock} className="btn-ghost-light" data-testid="admin-logout"><LogOut size={14} /><span>Lock</span></button>
        </div>

        {/* New client form */}
        <div className="mt-12 border border-white/10 p-6 md:p-8 bg-[#0a0a0a]">
          <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[var(--cyan)] mb-4 flex items-center gap-2">
            <Plus size={12} /> New Client
          </div>
          <form onSubmit={createClient} className="grid md:grid-cols-4 gap-6 items-end" data-testid="admin-new-client-form">
            <div>
              <label className="lucra-label">Business Name *</label>
              <input className="lucra-input mt-2" value={name} onChange={(e) => setName(e.target.value)} required data-testid="admin-client-name" />
            </div>
            <div>
              <label className="lucra-label">Location</label>
              <input className="lucra-input mt-2" placeholder="Atlanta, GA" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="admin-client-location" />
            </div>
            <div>
              <label className="lucra-label">Logo (PNG · JPG · WebP)</label>
              <input
                ref={logoRef}
                type="file"
                accept="image/*"
                onChange={(e) => setLogo(e.target.files?.[0] || null)}
                className="mt-2 block w-full text-sm text-white/80 file:mr-3 file:py-2 file:px-4 file:border file:border-white/15 file:bg-transparent file:text-white/70 file:font-mono-tech file:text-[10px] file:tracking-[0.2em] file:uppercase file:cursor-pointer hover:file:border-[var(--gold)]"
                required
                data-testid="admin-client-logo"
              />
            </div>
            <button type="submit" disabled={creating} className="btn-gold" data-testid="admin-client-create">
              <span>{creating ? "Creating…" : "Create Client"}</span>
            </button>
          </form>
        </div>

        {/* Existing clients */}
        <div className="mt-16 space-y-10">
          {loading && <div className="font-mono-tech text-white/40 text-sm">Loading…</div>}
          {!loading && clients.length === 0 && <div className="text-white/55">No clients yet.</div>}

          {clients.map((c) => (
            <div key={c.slug} className="border border-white/10 bg-[#0a0a0a]" data-testid={`admin-client-${c.slug}`}>
              <div className="flex items-center justify-between p-5 border-b border-white/10 flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden">
                    <img src={resolveUrl(c.logo_url)} alt="" className="max-w-full max-h-full object-contain" />
                  </div>
                  <div>
                    <div className="font-display text-xl text-white">{c.name}</div>
                    <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/45">
                      {c.location || "—"} · {(c.photos || []).length} photos · /clients/{c.slug}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/clients/${c.slug}`} className="btn-ghost-light" data-testid={`admin-view-${c.slug}`}>
                    <ExternalLink size={12} /><span>View</span>
                  </Link>
                  <label className="btn-cyan cursor-pointer">
                    <Upload size={12} />
                    <span>Add Photos</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadPhotos(c.slug, Array.from(e.target.files || []))}
                      data-testid={`admin-upload-${c.slug}`}
                    />
                  </label>
                  <button onClick={() => deleteClient(c.slug)} className="btn-ghost-light" data-testid={`admin-delete-${c.slug}`}>
                    <Trash2 size={12} /><span>Delete</span>
                  </button>
                </div>
              </div>

              <div className="p-5 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {(c.photos || []).map((p, i) => (
                  <div key={p.id || i} className="relative aspect-square bg-black/50 overflow-hidden group">
                    <img src={resolveUrl(p.url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    <button
                      onClick={() => deletePhoto(c.slug, p.id)}
                      className="absolute top-1 right-1 p-1.5 bg-black/70 border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity text-white/80 hover:text-red-400"
                      title="Delete photo"
                      data-testid={`admin-photo-delete-${p.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                {(c.photos || []).length === 0 && (
                  <div className="col-span-full font-mono-tech text-[10px] tracking-[0.22em] uppercase text-white/40">
                    No photos — upload some.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AdminClients;
